import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { db } from "../App";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import TagPill from "../components/TagPill";

const PORTFOLIO_SCROLL_KEY = "portfolioScrollY";

function safeStr(x) {
  return (x || "").toString();
}

function TwoLineClamp({ children, className = "" }) {
  return (
    <div
      className={className}
      style={{
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 2,
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  );
}

function Portfolio() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [tagFilter, setTagFilter] = useState("all");

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setErr("");

      try {
        const qy = query(
          collection(db, "portfolioItems"),
          where("published", "==", true),
          orderBy("sortOrder", "asc")
        );
        const snap = await getDocs(qy);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (mounted) setItems(rows);
      } catch (e) {
        console.error("Portfolio load error:", e);
        if (mounted) setErr(e?.message || "Failed to load portfolio.");
      }

      if (mounted) setLoading(false);
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    let raw;
    try {
      raw = sessionStorage.getItem(PORTFOLIO_SCROLL_KEY);
    } catch {
      return;
    }
    if (raw == null || raw === "") return;
    try {
      sessionStorage.removeItem(PORTFOLIO_SCROLL_KEY);
    } catch {
      /* ignore */
    }
    const y = Number.parseFloat(raw);
    if (!Number.isFinite(y) || y < 0) return;
    requestAnimationFrame(() => {
      window.scrollTo(0, y);
    });
  }, [loading, err]);

  const allTags = useMemo(() => {
    const s = new Set();
    items.forEach((it) => (it.tags || []).forEach((t) => s.add(safeStr(t).trim())));
    return Array.from(s).filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = useMemo(() => {
    if (tagFilter === "all") return items;
    return items.filter((it) => (it.tags || []).map((t) => safeStr(t)).includes(tagFilter));
  }, [items, tagFilter]);

  return (
    <div className="bg-secondary">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-4xl px-4 py-7 md:py-9">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Selected work</p>
          <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">Portfolio</h1>
          <div className="mt-4 h-px w-12 bg-slate-300/90" aria-hidden="true" />
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
            Representative Calgary residential projects — scope, constraints, and outcomes. Each entry
            highlights how integrated real estate, design, and construction thinking shows up in the work.
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-6xl space-y-7 px-4 py-8 md:py-10">
        <div className="rounded-xl border border-slate-200/80 bg-white p-3.5 shadow-sm md:p-4">
          <div className="flex flex-col gap-2.5 md:flex-row md:items-center md:gap-4">
            <label htmlFor="portfolio-tag-filter" className="text-sm font-semibold text-slate-700">
              Filter by tag
            </label>
            <select
              id="portfolio-tag-filter"
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm md:max-w-xs"
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
            >
              <option value="all">All</option>
              {allTags.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <div className="text-sm text-slate-500 md:ml-auto">
              Showing <span className="font-semibold text-slate-800">{filtered.length}</span>{" "}
              {filtered.length === 1 ? "project" : "projects"}
            </div>
          </div>
        </div>

        {loading && <div className="text-slate-600">Loading…</div>}
        {err && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{err}</div>}

        {!loading && !err && filtered.length === 0 && (
          <div className="rounded-xl border border-slate-200/80 bg-white px-5 py-7 text-center text-slate-600 shadow-sm">
            No published portfolio items yet.
          </div>
        )}

        {!loading && !err && filtered.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((it, idx) => {
              const slug = safeStr(it.slug);
              const title = safeStr(it.title) || "Untitled";
              const summary = safeStr(it.summary);
              const cover = safeStr(it.coverImageUrl);
              const tagList = (it.tags || []).map((t) => safeStr(t).trim()).filter(Boolean);
              const tagPreview = tagList.slice(0, 3);

              const aspectClass =
                idx % 6 === 0 ? "aspect-[16/10]" : idx % 6 === 3 ? "aspect-[4/3]" : "aspect-[3/2]";

              return (
                <Link
                  key={it.id}
                  to={`/portfolio/${encodeURIComponent(slug)}`}
                  onClick={() => {
                    try {
                      sessionStorage.setItem(PORTFOLIO_SCROLL_KEY, String(window.scrollY));
                    } catch {
                      /* ignore */
                    }
                  }}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm ring-1 ring-slate-200/40 transition hover:border-slate-300/90 hover:shadow-md"
                >
                  <div className={`relative w-full ${aspectClass} overflow-hidden bg-slate-100`}>
                    {cover ? (
                      <img
                        src={cover}
                        alt={title}
                        className="absolute inset-0 h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400">
                        No cover image
                      </div>
                    )}
                    <div
                      className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent opacity-80"
                      aria-hidden="true"
                    />
                  </div>

                  <div className="flex flex-1 flex-col gap-2.5 p-4">
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <h2 className="text-lg font-bold leading-snug tracking-tight text-slate-900 transition group-hover:text-slate-800">
                        {title}
                      </h2>
                      {tagPreview.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {tagPreview.map((t) => (
                            <TagPill key={t}>{t}</TagPill>
                          ))}
                        </div>
                      )}
                      {summary ? (
                        <TwoLineClamp className="text-sm leading-relaxed text-slate-600">{summary}</TwoLineClamp>
                      ) : null}
                    </div>
                    <div className="text-sm font-semibold text-slate-700 underline decoration-slate-300 underline-offset-2 transition group-hover:text-slate-900">
                      View project
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default Portfolio;
